import { uuidModule } from "@/modules/uuid";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { ICreateAudioRecordingOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncOperationKind } from "@/store/sync/types";
import {
  AudioRecordingModelData,
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings/types";

export class CreateAudioRecordingOperation extends BaseSyncOperation<ICreateAudioRecordingOperation> {
  get operationKind(): SyncOperationKind {
    return "CREATE_AUDIO_RECORDING";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const recordingStatus: AudioRecordingRecordingStatus = (this.payload.recording_status ??
      AudioRecordingRecordingStatus.INITIAL) as AudioRecordingRecordingStatus;
    const uploadStatus: AudioRecordingUploadStatus = (this.payload.upload_status ??
      AudioRecordingUploadStatus.INITIAL) as AudioRecordingUploadStatus;
    const processingStatus: AudioRecordingProcessingStatus = (this.payload.processing_status ??
      AudioRecordingProcessingStatus.INITIAL) as AudioRecordingProcessingStatus;

    const fallbackTimestamp = this.committedAt ?? new Date().toISOString();

    const optimisticUpdate: OptimisticSyncUpdate<AudioRecordingModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "AUDIO_RECORDING",
        model_version: 1,
        model_data: {
          id: this.payload.id,
          owned_by_space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
          recording_status: recordingStatus,
          upload_status: uploadStatus,
          processing_status: processingStatus,
          locally_created_at: fallbackTimestamp,
          locally_modified_at: fallbackTimestamp,
          associated_note_id: this.payload.associated_note_id ?? null,
          associated_client_id: this.payload.associated_client_id ?? null,
          value_json: { ...this.payload.value_json },
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    };

    return [optimisticUpdate];
  }

  async triggerRecompute() {
    await this.store.audioRecordings.recompute(this.payload.id);
  }
}
